import type { InsightResult } from '../models/insight.types'
import type { InsightPlatformOption } from '../models/insightDisplay.types'

import {
  outcomePresentation,
  summarizeSeverityBreakdown,
  type OutcomePresentation,
  type InsightOutcomeGroup,
  type PresentedOutcomeCategory,
  type SeverityBreakdownItem,
} from './insightPresentation'

export interface InsightCenterSummaryCard extends OutcomePresentation {
  outcome: PresentedOutcomeCategory
  count: number
  totalImpact: number
  severity: SeverityBreakdownItem[]
}

/**
 * One card per VISIBLE group — pass the same list the accordions render (ORP-20401).
 *
 * It used to map a hardcoded three-outcome constant, which meant an insight whose outcome was not
 * on that list (`roas_uplift`, or the `planning` value production actually stores) rendered its own
 * section while being counted in no card at all, and the three cards were emitted unconditionally
 * even at `count: 0`. v3 has no such split: one `visibleInsightSections` list feeds both the cards
 * and the sections (`views/App/Insights.vue:32-38,128-132`).
 */
export function summarizeCenterOutcomes(
  groups: readonly InsightOutcomeGroup[],
): InsightCenterSummaryCard[] {
  return groups.map((group) => ({
    ...outcomePresentation(group.key),
    outcome: group.key,
    count: group.items.length,
    totalImpact: group.totalImpact,
    severity: summarizeSeverityBreakdown(group.items),
  }))
}

/**
 * v3 parity (ORP-20401): a section whose aggregate impact is zero is hidden — UNLESS a filter or
 * search is active, in which case the user asked to see exactly this slice and hiding it would look
 * like a broken filter. `views/App/Insights.vue:128-132`.
 *
 * Note this is zero **impact**, not zero items: `groupInsightsByOutcome` already drops empty
 * groups, so what this removes is a group that has insights but no measured value behind them.
 */
export function visibleCenterOutcomeGroups(
  groups: readonly InsightOutcomeGroup[],
  filtersActive: boolean,
): InsightOutcomeGroup[] {
  if (filtersActive) return [...groups]
  return groups.filter((group) => Number(group.totalImpact) !== 0)
}

/**
 * v3 parity (ORP-20392): the live centre orders its accordions by **aggregate impact, descending**
 * — `views/App/Insights.vue:192` — so the biggest number leads regardless of which outcome it is.
 * This is deliberately data-driven, not a fixed list; `FIXED_OUTCOME_ORDER` stays the admin/history
 * order and is untouched. `Array.prototype.sort` is stable, so equal-impact groups keep the
 * `groupInsightsByOutcome` order and the result is still deterministic.
 */
export function orderCenterOutcomeGroups(
  groups: readonly InsightOutcomeGroup[],
): InsightOutcomeGroup[] {
  return [...groups].sort((left, right) => right.totalImpact - left.totalImpact)
}

export function deriveInsightPlatformOptions(
  insights: readonly InsightResult[],
): InsightPlatformOption[] {
  const labels = new Map<string, string>()
  for (const insight of insights) {
    const value = insight.platform?.trim()
    if (!value || value === 'all') continue
    const label = insight.platform_label?.trim() || value
    if (!labels.has(value)) labels.set(value, label)
  }
  return [...labels].map(([value, label]) => ({ value, label }))
}
